// src/hooks/useReviewerLogic.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// ✅ Cookie-auth services (no userId param)
import { getFolders, generateReviewer, saveReviewer, getUserReviewers } from "../services/api";

import { prepareReviewerPayload, prepareReviewerBatchPayloads } from "../services/reviewerLogic";
import { processUploadedFiles, validateAcademicStudyMaterial } from "../utils/fileHelpers";
import { generatePDFDocument } from "../utils/pdfGenerator";

export const useReviewerLogic = () => {
  const navigate = useNavigate();

  // State
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [view, setView] = useState("form");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSavedToDashboard, setHasSavedToDashboard] = useState(false);
  const [loadingText, setLoadingText] = useState("Initializing...");
  const [generationStep, setGenerationStep] = useState(0);


  // Optional UI user (NOT for auth)
  const [user, setUser] = useState(null);

  // Form Data
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [textInput, setTextInput] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [rawContent, setRawContent] = useState("");
  const [generatedData, setGeneratedData] = useState(null);
  const [existingReviewerNames, setExistingReviewerNames] = useState([]);

  // ✅ AI Config
  const [aiConfig, setAiConfig] = useState({
    summaryEnabled: true,
    flashcardsEnabled: true,
    quizEnabled: true,
    difficulty: 2,
    quizType: ["mcq"],
    questionCount: 10,
    flashcardCount: 10,
    timePerQuestion: 30,
    customInstructions: "",
    eli5: false,
    batchEnabled: true,
    maxCharsPerBatch: 20000,
    overlapChars: 1000,
    totalQuestionsMode: true,
    totalFlashcardsMode: true,
    maxQuestionsPerBatch: 60,
    maxFlashcardsPerBatch: 50,
    autoBatchThresholdChars: 18000,
    targetMaxBatches: 24,
    maxMaxCharsPerBatch: 24000,
    throttleMs: 300,
    maxRetries: 3,
  });

  const updateConfig = (key, val) => setAiConfig((prev) => ({ ...prev, [key]: val }));

  // Helpers
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const normalizeQuizText = (v) =>
    String(v ?? "")
      // Keep escaped currency symbols as real currency.
      .replace(/\\\$/g, "$")
      // Convert common LaTeX text blocks to plain text.
      .replace(/\\text\s*\{([^}]*)\}/g, "$1")
      // Common LaTeX math operators/symbols.
      .replace(/\\times/g, "×")
      .replace(/\\div/g, "÷")
      .replace(/\\cdot/g, "·")
      .replace(/\\pm/g, "±")
      .replace(/\\geq|\\ge/g, ">=")
      .replace(/\\leq|\\le/g, "<=")
      .replace(/\\neq/g, "!=")
      .replace(/\\left|\\right/g, "")
      // Basic fraction/square-root rendering.
      .replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, "($1)/($2)")
      .replace(/\\sqrt\s*\{([^{}]+)\}/g, "sqrt($1)")
      // Unwrap inline/display math delimiters but keep inner content.
      .replace(/\$([^$]+)\$/g, "$1")
      .replace(/\\%/g, "%")
      .replace(/\\\(/g, "")
      .replace(/\\\)/g, "")
      .replace(/\\\[/g, "")
      .replace(/\\\]/g, "")
      // Remove stray escape slash before digits (e.g., "\12,500" -> "12,500").
      .replace(/\\(?=\d)/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const normalizeQuizItems = (quizItems) =>
    (Array.isArray(quizItems) ? quizItems : []).map((q) => {
      const question = normalizeQuizText(q?.question);
      let options = Array.isArray(q?.options) ? q.options.map(normalizeQuizText) : [];
      let answer = normalizeQuizText(q?.answer);
      const normalizeCmp = (s) =>
        String(s || "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

      // Remove leading option labels accidentally generated by AI (e.g., "A. ", "B) ").
      options = options.map((opt) => opt.replace(/^[A-Da-d]\s*[\.\):-]\s*/, "").trim());
      answer = answer.replace(/^[A-Da-d]\s*[\.\):-]\s*/, "").trim();

      // If all options share the same variable assignment prefix (e.g., "x = "), trim it.
      const varPrefixPattern = /^([a-zA-Z])\s*=\s*/;
      const prefixes = options
        .map((opt) => opt.match(varPrefixPattern)?.[0] || null)
        .filter(Boolean);
      const sharedPrefix =
        options.length > 0 && prefixes.length === options.length && prefixes.every((p) => p === prefixes[0])
          ? prefixes[0]
          : null;

      if (sharedPrefix) {
        const esc = sharedPrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const re = new RegExp(`^${esc}`);
        options = options.map((opt) => opt.replace(re, "").trim());
        answer = answer.replace(re, "").trim();
      }

      // Force answer to resolve to one exact option to avoid scoring mismatches.
      if (options.length > 0 && answer) {
        const letterMatch = answer.match(/^[A-H]$/i);
        const numberMatch = answer.match(/^\d+$/);

        if (letterMatch) {
          const idx = letterMatch[0].toUpperCase().charCodeAt(0) - 65;
          if (idx >= 0 && idx < options.length) answer = options[idx];
        } else if (numberMatch) {
          const idx = Number(numberMatch[0]) - 1;
          if (idx >= 0 && idx < options.length) answer = options[idx];
        } else {
          const normAnswer = normalizeCmp(answer);
          const exact = options.find((opt) => normalizeCmp(opt) === normAnswer);
          if (exact) {
            answer = exact;
          } else {
            const fuzzy = options.filter((opt) => {
              const n = normalizeCmp(opt);
              return n.includes(normAnswer) || normAnswer.includes(n);
            });
            if (fuzzy.length === 1) answer = fuzzy[0];
          }
        }
      }

      return {
        ...q,
        question,
        options,
        answer,
        explanation: normalizeQuizText(q?.explanation),
      };
    });
  const htmlToText = (s) =>
    String(s || "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(div|li|ul|h3)>/gi, "\n")
      .replace(/<[^>]*>/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]+/g, " ")
      .trim();

  const extractErrText = (err) => {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      String(err);
    return String(msg || "");
  };

  const extractStatus = (err) => err?.response?.status || err?.status || null;

  const normalizeName = (value) =>
    String(value ?? "")
      .trim()
      .replace(/\s+/g, " ");
  const MAX_REVIEWER_NAME_LENGTH = 30;

  const hasAtLeastThreeLetters = (value) => {
    const letters = String(value ?? "").match(/[A-Za-z]/g);
    return Array.isArray(letters) && letters.length >= 3;
  };

  const isDuplicateReviewerName = (value) => {
    const needle = normalizeName(value).toLowerCase();
    if (!needle) return false;
    return existingReviewerNames.some((name) => normalizeName(name).toLowerCase() === needle);
  };

  const validateReviewerTitle = () => {
    const cleanTitle = normalizeName(title);

    if (!cleanTitle) {
      Swal.fire("Validation error", "Reviewer title is required.", "warning");
      return { isValid: false, cleanTitle: "" };
    }

    if (cleanTitle.length > MAX_REVIEWER_NAME_LENGTH) {
      Swal.fire(
        "Validation error",
        `Reviewer title must not exceed ${MAX_REVIEWER_NAME_LENGTH} characters.`,
        "warning",
      );
      return { isValid: false, cleanTitle };
    }

    if (!hasAtLeastThreeLetters(cleanTitle)) {
      Swal.fire("Validation error", "Reviewer title must contain at least 3 letters.", "warning");
      return { isValid: false, cleanTitle };
    }

    if (isDuplicateReviewerName(cleanTitle)) {
      Swal.fire("Validation error", "Reviewer title already exists.", "warning");
      return { isValid: false, cleanTitle };
    }

    return { isValid: true, cleanTitle };
  };

  // ✅ If cookie expired, redirect
  const handleAuthError = () => {
    localStorage.removeItem("user"); // UI-only
    navigate("/login");
  };

  // ✅ UI-only user load (not required for auth)
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) return;
    try {
      setUser(JSON.parse(stored));
    } catch {
      // ignore
    }
  }, []);

  // ✅ Load folders via cookie session
  useEffect(() => {
    const fetchFolders = async () => {
      setFoldersLoading(true);
      try {
        const res = await getFolders();

        let list = [];
        if (Array.isArray(res)) list = res;
        else if (Array.isArray(res?.folders)) list = res.folders;
        else if (Array.isArray(res?.data)) list = res.data;
        else if (Array.isArray(res?.data?.folders)) list = res.data.folders;

        setFolders(list);
      } catch (err) {
        console.error("Folder fetch error", err);
        const status = extractStatus(err);
        if (status === 401) {
          handleAuthError();
          return;
        }
        setFolders([]);
      } finally {
        setFoldersLoading(false);
      }
    };

    fetchFolders();
  }, []);

  useEffect(() => {
    const fetchReviewerNames = async () => {
      try {
        const res = await getUserReviewers();
        let reviewers = [];
        if (Array.isArray(res)) reviewers = res;
        else if (Array.isArray(res?.data)) reviewers = res.data;
        else if (Array.isArray(res?.reviewers)) reviewers = res.reviewers;
        else if (Array.isArray(res?.data?.reviewers)) reviewers = res.data.reviewers;

        const names = reviewers
          .map((r) => String(r?.topic || r?.title || "").trim())
          .filter(Boolean);
        setExistingReviewerNames(names);
      } catch (err) {
        const status = extractStatus(err);
        if (status === 401) {
          handleAuthError();
          return;
        }
        setExistingReviewerNames([]);
      }
    };

    fetchReviewerNames();
  }, []);

  // ============================================
  // ✅ FIXED: PDF-safe Content Validation Logic
  // ============================================
  const validateContent = (text) => {
    if (!text) {
      return {
        isValid: false,
        title: "Empty Content",
        message: "Please enter text or upload a file.",
      };
    }

    const trimmed = String(text).trim();
    if (trimmed.length < 50) {
      return {
        isValid: false,
        title: "Content Too Short",
        message: `You only have ${trimmed.length} characters. We need at least 50 to generate a good review.`,
      };
    }

    // Keep original structure for AI/chunking; use a stricter copy only for validation checks.
    const normalized = trimmed
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    const validationText = normalized
      .replace(/\s+/g, " ")
      .replace(/[^\p{L}\p{N}\s]/gu, "")
      .trim();

    // If after cleanup there's almost nothing, extraction failed
    if (validationText.length < 50) {
      return {
        isValid: false,
        title: "Unreadable PDF Text",
        message:
          "We couldn’t extract readable text from your file. If this is a scanned PDF (image), OCR is required. Try a text-based PDF or paste your text.",
      };
    }

    // ✅ Detect scanned/image PDF (too few letters)
    const letters = (validationText.match(/\p{L}/gu) || []).length;
    const ratioLetters = letters / Math.max(1, validationText.length);

    if (ratioLetters < 0.2) {
      return {
        isValid: false,
        title: "Scanned / Image PDF Detected",
        message:
          "This PDF looks like an image (scanned). Text extraction won't work unless you use OCR. Try a text-based PDF or copy/paste the text.",
      };
    }

    // ✅ Detect corrupted extraction: long repeated runs
    const hasLongRun = /(.)\1{10,}/.test(validationText);
    if (hasLongRun) {
      return {
        isValid: false,
        title: "Corrupted Extraction",
        message:
          "The extracted text contains long repeated characters (looks corrupted). Try exporting the PDF again or paste the text.",
      };
    }

    // ✅ Gibberish smash check (after cleanup)
    const words = validationText.split(/\s+/);
    const longestWord = words.reduce((a, b) => (a.length > b.length ? a : b), "").length;

    if (longestWord > 60) {
      return {
        isValid: false,
        title: "Gibberish Detected",
        message:
          "The extracted content contains unusually long strings without spaces. Please provide real text notes.",
      };
    }

    return { isValid: true, normalized };
  };

  // Merge batch outputs
  const mergeBatchOutputs = (batchOutputs) => {
    const safeBatches = Array.isArray(batchOutputs) ? batchOutputs : [];

    const combinedSummary = safeBatches
      .map((b) => {
        const s = String(b?.summary || "");
        return `<div style="margin-bottom: 20px;">${s}</div>`;
      })
      .join("");

    const allFlashcardsRaw = safeBatches.flatMap((b) =>
      Array.isArray(b?.flashcards) ? b.flashcards : []
    );

    const flashcards = allFlashcardsRaw.map((c, idx) => ({
      id: idx + 1,
      front: String(c?.front ?? ""),
      back: String(c?.back ?? ""),
    }));

    const quiz = safeBatches
      .flatMap((b) => (Array.isArray(b?.quiz) ? b.quiz : []))
      .map((q) => ({
        question: String(q?.question ?? ""),
        options: Array.isArray(q?.options) ? q.options.map(String) : [],
        answer: String(q?.answer ?? ""),
        explanation: String(q?.explanation ?? ""),
      }));

    const firstAnalyticsTopic =
      safeBatches.find(
        (b) => typeof b?.analyticsTopic === "string" && b.analyticsTopic.trim()
      )?.analyticsTopic || null;

    return {
      summary: combinedSummary,
      flashcards,
      quiz: normalizeQuizItems(quiz),
      batches: safeBatches,
      analyticsTopic: firstAnalyticsTopic,
    };
  };

  const trimMergedToTotals = (merged) => {
    const out = { ...merged };

    if (aiConfig.quizEnabled && aiConfig.totalQuestionsMode) {
      const totalQ = Math.max(0, Math.floor(Number(aiConfig.questionCount ?? 0)));
      if (Number.isFinite(totalQ)) out.quiz = Array.isArray(out.quiz) ? out.quiz.slice(0, totalQ) : [];
    }

    if (aiConfig.flashcardsEnabled && aiConfig.totalFlashcardsMode) {
      const totalF = Math.max(0, Math.floor(Number(aiConfig.flashcardCount ?? 0)));
      if (Number.isFinite(totalF))
        out.flashcards = Array.isArray(out.flashcards) ? out.flashcards.slice(0, totalF) : [];
    }

    if (Array.isArray(out.flashcards)) {
      out.flashcards = out.flashcards.map((c, idx) => ({
        id: idx + 1,
        front: String(c?.front ?? ""),
        back: String(c?.back ?? ""),
      }));
    }

    return out;
  };

  const buildCoverageMergePayload = (batchOutputs) => {
    const coverageSource = (Array.isArray(batchOutputs) ? batchOutputs : [])
      .map((b, i) => {
        const summaryText = htmlToText(b?.summary || "");
        return `BATCH ${i + 1} SUMMARY:\n${summaryText}`;
      })
      .filter(Boolean)
      .join("\n\n---\n\n");

    if (!coverageSource || coverageSource.length < 100) return null;

    const mergeConfig = {
      ...aiConfig,
      summaryEnabled: true,
      flashcardsEnabled: false,
      quizEnabled: false,
      customInstructions: [
        "Create a single final master summary from the provided batch summaries.",
        "Do not omit unique facts that appear in any batch.",
        "Merge duplicates, keep the most complete version, and preserve technical details.",
        "Use clear module structure and complete sentences only.",
      ].join(" "),
    };

    return prepareReviewerPayload(coverageSource, mergeConfig);
  };

  // Retry detection
  const isDailyQuotaExceeded = (errText) => {
    const t = String(errText || "").toLowerCase();
    return (
      t.includes("quota exceeded") ||
      t.includes("exceeded your current quota") ||
      t.includes("requests/day") ||
      t.includes("per day")
    );
  };

  const isTempRateLimit = (status, errText) => {
    const t = String(errText || "").toLowerCase();
    return status === 429 || t.includes("too many requests") || t.includes("rate limit");
  };

  const isIncompleteOutputError = (status, errText) => {
    const t = String(errText || "").toLowerCase();
    return (
      status === 422 ||
      t.includes("output remained incomplete") ||
      t.includes("incomplete output") ||
      t.includes("summary too short")
    );
  };

  const parseRetryDelayMs = (errText) => {
    const t = String(errText || "");
    const m1 = t.match(/retry in\s+(\d+(?:\.\d+)?)s/i);
    if (m1?.[1]) return Math.max(0, Math.round(Number(m1[1]) * 1000));
    return null;
  };

  const generateReviewerWithRetry = async (payload, attempt = 0) => {
    try {
      return await generateReviewer(payload);
    } catch (err) {
      const status = extractStatus(err);
      const errText = extractErrText(err);

      if (status === 401) {
        handleAuthError();
        throw err;
      }

      if (isDailyQuotaExceeded(errText)) {
        const e = new Error("Daily Gemini quota reached. Reduce batch count or upgrade billing.");
        e.__dailyQuota = true;
        e.__original = errText;
        throw e;
      }

      const shouldRetry = isTempRateLimit(status, errText) && attempt < Number(aiConfig.maxRetries || 3);
      if (!shouldRetry) throw err;

      const parsedDelay = parseRetryDelayMs(errText);
      const backoff = Math.min(15000, 1000 * Math.pow(2, attempt));
      const waitMs = parsedDelay ?? backoff;

      setLoadingText(`Rate-limited. Retrying... (${attempt + 1})`);
      await sleep(waitMs);

      return generateReviewerWithRetry(payload, attempt + 1);
    }
  };

  const splitContentForRetry = (text) => {
    const src = String(text || "").trim();
    if (!src) return ["", ""];
    if (src.length < 2000) return [src, ""];

    const mid = Math.floor(src.length / 2);
    const paraBreak = src.lastIndexOf("\n\n", mid);
    const sentenceBreak = src.lastIndexOf(". ", mid);
    const cut = paraBreak > 1000 ? paraBreak : sentenceBreak > 1000 ? sentenceBreak + 1 : mid;
    return [src.slice(0, cut).trim(), src.slice(cut).trim()];
  };

  const mergeSplitResults = (left, right) => ({
    summary: [String(left?.summary || ""), String(right?.summary || "")].filter(Boolean).join("<br><br>"),
    flashcards: [
      ...(Array.isArray(left?.flashcards) ? left.flashcards : []),
      ...(Array.isArray(right?.flashcards) ? right.flashcards : []),
    ],
    quiz: [
      ...(Array.isArray(left?.quiz) ? left.quiz : []),
      ...(Array.isArray(right?.quiz) ? right.quiz : []),
    ],
  });

  const generateWithSplitFallback = async (payload, depth = 0) => {
    try {
      return await generateReviewerWithRetry(payload);
    } catch (err) {
      const status = extractStatus(err);
      const errText = extractErrText(err);
      const content = String(payload?.content || "");
      const canSplit = content.length >= 2500 && depth < 2;

      if (!isIncompleteOutputError(status, errText) || !canSplit) throw err;

      setLoadingText(`Batch incomplete. Splitting and retrying (${depth + 1})...`);
      const [leftText, rightText] = splitContentForRetry(content);
      if (!leftText || !rightText) throw err;

      const leftPayload = {
        ...payload,
        content: leftText,
      };
      const rightPayload = {
        ...payload,
        content: rightText,
      };

      const leftRes = await generateWithSplitFallback(leftPayload, depth + 1);
      const rightRes = await generateWithSplitFallback(rightPayload, depth + 1);
      return mergeSplitResults(leftRes, rightRes);
    }
  };

  // Handlers
  const handleGenerate = async (activeTab) => {
    if (!aiConfig.summaryEnabled && !aiConfig.flashcardsEnabled && !aiConfig.quizEnabled) {
      return Swal.fire("Error", "Select at least one feature.", "warning");
    }

    const titleValidation = validateReviewerTitle();
    if (!titleValidation.isValid) return;
    if (titleValidation.cleanTitle !== title) setTitle(titleValidation.cleanTitle);

    setIsProcessing(true);
    setHasSavedToDashboard(false);
    setGenerationStep(0);
    setLoadingText("Reading files...");

    // ✅ DEBUG: Check Hard Mode Activation
    if (aiConfig.difficulty === 3) {
      console.warn("🔥 HARD MODE ACTIVATED: Expect strict choice balancing and tougher questions.");
    }

    try {
      const raw =
        activeTab === "text" ? textInput : await processUploadedFiles(uploadedFiles);

      if (!raw || String(raw).trim().length === 0) {
        throw new Error("No content to process. Upload a file or paste text.");
      }

      setGenerationStep(1);
      setLoadingText("Validating content quality...");

      if (activeTab === "file") {
        const academicCheck = validateAcademicStudyMaterial(raw);
        if (!academicCheck.isValid) {
          return Swal.fire({
            icon: "warning",
            title: academicCheck.title,
            text: academicCheck.message,
            confirmButtonColor: "#f59e0b",
          });
        }
      }

      // ✅ Debug preview (helps you confirm PDF extraction is real text)
      console.log("EXTRACTED CONTENT PREVIEW:", String(raw).slice(0, 500));

      // ✅ Validate & use normalized output for AI
      const validation = validateContent(raw);
      if (!validation.isValid) {
        return Swal.fire({
          icon: "warning",
          title: validation.title,
          text: validation.message,
          confirmButtonColor: "#f59e0b",
        });
      }

      const normalized = validation.normalized || String(raw).trim();
      setRawContent(normalized);

      const shouldBatch =
        !!aiConfig.batchEnabled &&
        normalized.length >= Number(aiConfig.autoBatchThresholdChars || aiConfig.maxCharsPerBatch || 18000);

      if (shouldBatch) {
        setGenerationStep(2);
        setLoadingText("Chunking content into batches...");

        const targetMaxBatches = Number(aiConfig.targetMaxBatches || 24);
        const maxMaxChars = Number(aiConfig.maxMaxCharsPerBatch || 24000);

        let maxChars = Number(aiConfig.maxCharsPerBatch || 20000);

        let pack = prepareReviewerBatchPayloads(normalized, aiConfig, {
          maxCharsPerBatch: maxChars,
          overlapChars: Number(aiConfig.overlapChars || 1000),
          totalQuestionsMode: !!aiConfig.totalQuestionsMode,
          totalFlashcardsMode: !!aiConfig.totalFlashcardsMode,
          maxQuestionsPerBatch: Number(aiConfig.maxQuestionsPerBatch || 60),
          maxFlashcardsPerBatch: Number(aiConfig.maxFlashcardsPerBatch || 50),
        });

        while (pack.batchCount > targetMaxBatches && maxChars < maxMaxChars) {
          maxChars = Math.min(maxMaxChars, Math.floor(maxChars * 1.2));
          pack = prepareReviewerBatchPayloads(normalized, aiConfig, {
            maxCharsPerBatch: maxChars,
            overlapChars: Number(aiConfig.overlapChars || 1000),
            totalQuestionsMode: !!aiConfig.totalQuestionsMode,
            totalFlashcardsMode: !!aiConfig.totalFlashcardsMode,
            maxQuestionsPerBatch: Number(aiConfig.maxQuestionsPerBatch || 60),
            maxFlashcardsPerBatch: Number(aiConfig.maxFlashcardsPerBatch || 50),
          });
        }

        const { payloads, batchCount } = pack;

        if (batchCount > targetMaxBatches) {
          throw new Error(
            `Too many batches (${batchCount}). Increase maxCharsPerBatch (current ${maxChars}) or reduce overlapChars.`
          );
        }

        setLoadingText(`Prepared ${batchCount} batches. Starting AI...`);

        const batchResults = [];
        const throttleMs = Number(aiConfig.throttleMs || 0);

        for (let i = 0; i < payloads.length; i++) {
          setGenerationStep(i === 0 ? 2 : 3);
          setLoadingText(`Analyzing batch ${i + 1}/${batchCount} with AI...`);
          const res = await generateWithSplitFallback(payloads[i]);
          batchResults.push({
            ...res,
            batchMeta: payloads[i]?.batchMeta || { batchIndex: i, batchCount },
          });
          if (throttleMs > 0 && i < payloads.length - 1) await sleep(throttleMs);
        }

        setGenerationStep(3);
        const merged = mergeBatchOutputs(batchResults);
        const trimmed = trimMergedToTotals(merged);

        // Final synthesis pass to improve coverage across all batches.
        if (aiConfig.summaryEnabled) {
          const mergePayload = buildCoverageMergePayload(batchResults);
          if (mergePayload) {
            try {
              setLoadingText("Merging batch summaries for full coverage...");
              const mergedSummaryRes = await generateWithSplitFallback(mergePayload);
              const mergedSummary = String(mergedSummaryRes?.summary || "").trim();
              if (mergedSummary) trimmed.summary = mergedSummary;
            } catch (mergeErr) {
              console.warn("Coverage merge pass skipped:", extractErrText(mergeErr));
            }
          }
        }

        setGenerationStep(4);
        setLoadingText("Finalizing reviewer...");
        setGeneratedData(trimmed);
        setView("dashboard");
        return;
      }

      setGenerationStep(2);
      setLoadingText("Formulating prompt and analyzing with AI...");
      const payload = prepareReviewerPayload(normalized, aiConfig);

      setGenerationStep(3);
      setLoadingText("Generating study materials & questions with AI...");
      const result = await generateReviewerWithRetry(payload);

      setGenerationStep(4);
      setLoadingText("Finalizing & assembling reviewer...");
      setGeneratedData({
        ...result,
        quiz: normalizeQuizItems(result?.quiz),
      });
      setView("dashboard");
    } catch (error) {
      const errText = extractErrText(error);

      if (error?.__dailyQuota) {
        return Swal.fire("Quota reached", error.message, "warning");
      }

      if (extractStatus(error) === 401) {
        handleAuthError();
        return;
      }

      Swal.fire("Error", errText, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!generatedData) return;
    if (isSaving || hasSavedToDashboard) return;

    const titleValidation = validateReviewerTitle();
    if (!titleValidation.isValid) return;

    setIsSaving(true);

    Swal.fire({
      title: "Saving...",
      text: "Encrypting & Uploading...",
      didOpen: () => Swal.showLoading(),
    });

    try {
      const themeColors = ["blue", "orange", "purple", "emerald", "red"];
      const randomColor = themeColors[Math.floor(Math.random() * themeColors.length)];

      // ✅ No userId from localStorage; backend should use req.user
      const payload = {
        topic: titleValidation.cleanTitle,
        folder: folderId || null,
        color: randomColor,
        analyticsTopic: generatedData?.analyticsTopic || null,
        generatedData: {
          summary: aiConfig.summaryEnabled ? generatedData.summary || "" : "",
          flashcards: aiConfig.flashcardsEnabled ? generatedData.flashcards || [] : [],
          quiz: aiConfig.quizEnabled ? generatedData.quiz || [] : [],
          analyticsTopic: generatedData?.analyticsTopic || null,
          batches: Array.isArray(generatedData?.batches) ? generatedData.batches : undefined,
        },
        config: aiConfig,
      };

      const result = await saveReviewer(payload);

      Swal.fire({
        icon: "success",
        title: "Saved Successfully!",
        html: `<div style="text-align:left;font-size:14px;">
                <p><b>Saved:</b> ${result?.reviewer?.topic || "Reviewer"}</p>
              </div>`,
      });
      setHasSavedToDashboard(true);
      setExistingReviewerNames((prev) => [...prev, titleValidation.cleanTitle]);
    } catch (error) {
      if (extractStatus(error) === 401) {
        handleAuthError();
        return;
      }
      Swal.fire("Error", extractErrText(error), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => generatePDFDocument({ title, generatedData, config: aiConfig });

  const updateGeneratedQuiz = (updaterOrList) => {
    setGeneratedData((prev) => {
      if (!prev) return prev;
      const nextQuiz =
        typeof updaterOrList === "function"
          ? updaterOrList(prev.quiz || [])
          : updaterOrList;
      return {
        ...prev,
        quiz: Array.isArray(nextQuiz) ? nextQuiz : [],
      };
    });
  };

  const resetForm = () => {
    setView("form");
    setGeneratedData(null);
    setTextInput("");
    setUploadedFiles([]);
    setRawContent("");
    setHasSavedToDashboard(false);
    setIsSaving(false);
  };

  return {
    state: {
      user, // UI only
      folders,
      foldersLoading,
      view,
      isProcessing,
      isSaving,
      hasSavedToDashboard,
      loadingText,
      generationStep,
      title,
      folderId,
      textInput,
      uploadedFiles,
      rawContent,
      generatedData,
      aiConfig,
    },
    actions: {
      setTitle,
      setFolderId,
      setTextInput,
      setUploadedFiles,
      updateConfig,
      updateGeneratedQuiz,
      handleGenerate,
      handleSave,
      handleDownload,
      resetForm,
    },
  };
};
